
import * as React from "react";
import gsap from "gsap";

const CSS = `
.scalio-navbar {
  font-family: Outfit, system-ui, sans-serif;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  color: #f4f4f5;
}

.scalio-navbar * {
  margin: 0;
  padding: 0;
}

.scalio-navbar a {
  color: inherit;
  text-decoration: none;
}

/* BUTTON — ORIGINAL DESKTOP STYLE */

.scalio-navbar .btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 16px 30px;
  border-radius: 999px;
  font-weight: 500;
  font-size: 16px;
  color: #fff;
  background: linear-gradient(135deg, #2C3480, #3a47a8);
  box-shadow: 0 10px 30px rgba(44, 52, 128, 0.35);
  transition: box-shadow .3s, color .3s;
  will-change: transform;
}

.scalio-navbar .btn::after {
  content: "";
  position: absolute;
  inset: 0;
  background: #fff;
  transform: translateY(101%);
  transition: transform .4s cubic-bezier(.2,.8,.2,1);
  border-radius: inherit;
}

.scalio-navbar .btn span {
  position: relative;
  z-index: 1;
}

.scalio-navbar .btn:hover {
  color: #111;
  box-shadow: 0 14px 40px rgba(44, 52, 128, 0.5);
}

.scalio-navbar .btn:hover::after {
  transform: translateY(0);
}

.scalio-navbar .btn.sm {
  padding: 10px 22px;
  font-size: 14px;
  white-space: nowrap;
}

.scalio-navbar .btn.full {
  width: 100%;
  margin-top: 18px;
}

.scalio-navbar .btn.ghost {
  background: rgba(255,255,255,.06);
  border: 1px solid rgba(255,255,255,.14);
  box-shadow: none;
  backdrop-filter: blur(14px);
}

/* NAVBAR — DESKTOP UNCHANGED */

.scalio-navbar .nav {
  position: fixed;
  top: 16px;
  left: 50%;
  translate: -50% 0;
  width: min(1180px, calc(100% - 32px));
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 22px;
  border-radius: 999px;
  transition:
    background .4s,
    backdrop-filter .4s,
    border-color .4s;
  border: 1px solid transparent;
}

.scalio-navbar .nav.scrolled {
  background: rgba(20,20,24,.6);
  backdrop-filter: blur(10px);
  border-color: rgba(255,255,255,.14);
}

/* LOGO — DESKTOP UNCHANGED */

.scalio-navbar .logo {
  font-weight: 700;
  font-size: 24px;
  letter-spacing: -0.5px;
}

.scalio-navbar .logo {
  display: inline-flex;
  align-items: center;
}

.scalio-navbar .logo-img {
  display: block;
  height: 46px;
  width: auto;
}

/* NAVIGATION LINKS — DESKTOP UNCHANGED */

.scalio-navbar .nav nav {
  display: flex;
  gap: 30px;
  font-size: 14px;
  color: #9a9aa3;
}

.scalio-navbar .nav nav a {
  position: relative;
  transition: color .3s;
}

.scalio-navbar .nav nav a::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: -4px;
  height: 2px;
  width: 100%;
  background: #2C3480;
  transform: scaleX(0);
  transform-origin: right;
  transition: transform .35s;
}

.scalio-navbar .nav nav a:hover {
  color: #ffffff;
}

.scalio-navbar .nav nav a:hover::after {
  transform: scaleX(1);
  transform-origin: left;
}

/* MOBILE ONLY — HIDE NAV LINKS */

@media (max-width: 900px) {
  .scalio-navbar .nav nav {
    display: none;
  }
}

/* SMALL MOBILE — ORIGINAL STYLE */

@media (max-width: 600px) {
  .scalio-navbar .nav {
    top: 10px;
    width: calc(100% - 20px);
    padding: 8px 10px 8px 18px;
  }

  .scalio-navbar .logo-img {
    height: auto;
    width: min(118px, 36vw);
  }

  .scalio-navbar .btn.sm {
    padding: 9px 16px;
    font-size: 13px;
  }
}

.scalio-navbar {
  position: relative;
  z-index: 50;
}

.scalio-navbar,
.scalio-navbar * {
  box-sizing: border-box;
}
`;

/* MAGNETIC BUTTON — ORIGINAL LOGIC */

function Magnetic({
  children,
  className = "",
  href = "#",
  onClick,
}) {
  const ref = React.useRef(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const xTo = gsap.quickTo(el, "x", {
      duration: 0.5,
      ease: "power3",
    });

    const yTo = gsap.quickTo(el, "y", {
      duration: 0.5,
      ease: "power3",
    });

    const move = (e) => {
      const r = el.getBoundingClientRect();

      xTo((e.clientX - r.left - r.width / 2) * 0.3);
      yTo((e.clientY - r.top - r.height / 2) * 0.3);
    };

    const leave = () => {
      xTo(0);
      yTo(0);
    };

    el.addEventListener("mousemove", move);
    el.addEventListener("mouseleave", leave);

    return () => {
      el.removeEventListener("mousemove", move);
      el.removeEventListener("mouseleave", leave);
    };
  }, []);

  return (
    <a
      ref={ref}
      href={href}
      onClick={onClick}
      className={`btn ${className}`}
    >
      <span>{children}</span>
    </a>
  );
}

/* HERO / NAVBAR */

export default function Hero({
  logoSrc = "/scalio-logo.webp",
  className = "",
}) {
  const root = React.useRef(null);

  React.useEffect(() => {
    const nav = root.current?.querySelector(".nav");
    if (!nav) return;

    const tween = gsap.from(nav, {
      y: -60,
      opacity: 0,
      duration: 1,
      ease: "power4.out",
    });

    const sync = () => {
      nav.classList.toggle(
        "scrolled",
        window.scrollY > 80
      );
    };

    sync();

    window.addEventListener("scroll", sync, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", sync);
      tween.revert();
    };
  }, []);

  return (
    <div
      ref={root}
      id="top"
      className={"scalio-navbar " + className}
    >
      <style>{CSS}</style>

      <header className="nav">
        {/* LOGO */}
        <a
          className="logo"
          href="#top"
          aria-label="Scalio USDT Card"
        >
          <img
            className="logo-img"
            src={logoSrc}
            alt="Scalio USDT Card"
          />
        </a>

        {/* DESKTOP NAVIGATION */}
        <nav>
          {[
            ["Features", "#features"],
            ["Cards", "#cards"],
            ["Instant Bonus", "#how"],
            ["Security", "#security"],
            ["FAQs", "#faq"],
          ].map(([label, href]) => (
            <a key={label} href={href}>
              {label}
            </a>
          ))}
        </nav>

        {/* GET CARD BUTTON */}
        <Magnetic className="sm" href="#cards">
          Get Card →
        </Magnetic>
      </header>
    </div>
  );
}
